
import { HailingStrategy, CommunicationConfig, Order, Driver, OperatingContext, AudioClip, SystemConfig } from "../types";

// --- HELPERS ---

const base64ToBlob = (base64: string, mimeType: string): Blob => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
};

// Expanded Template Engine
export const hydrateTemplate = (template: string, order: Order, driver: Driver | { name: string }, config: SystemConfig): string => {
    let text = template;

    // Variáveis de Pessoa/Entidade
    text = text.replace(/\[Nome\]/gi, order.customerName.split(' ')[0]);
    text = text.replace(/\[Cliente\]/gi, order.customerName.split(' ')[0]);
    text = text.replace(/\[Motorista\]/gi, driver.name);
    text = text.replace(/\[Entregador\]/gi, driver.name);
    text = text.replace(/\[Nome da Loja\]/gi, config.storeName || 'Nossa Loja');
    text = text.replace(/\[Loja\]/gi, config.storeName || 'Nossa Loja');

    // Variáveis de Pedido
    text = text.replace(/\[ID\]/gi, order.id);
    text = text.replace(/\[Valor\]/gi, order.orderValue ? `R$ ${order.orderValue.toFixed(2)}` : 'R$ 0,00');
    text = text.replace(/\[Pagamento\]/gi, order.paymentMethod || 'A Confirmar');

    // Variáveis de Logística
    text = text.replace(/\[Tempo\]/gi, order.estimatedDuration ? `${Math.round(order.estimatedDuration)} min` : 'alguns minutos');
    text = text.replace(/\[Bairro\]/gi, order.neighborhood || 'seu bairro');

    return text;
};

// --- CORE SERVICE (DETERMINISTIC - NO AI) ---

export const handleIFoodExternalConfirmation = async (order: Order) => {
    const iFoodId = order.ifoodSupportId?.trim();
    const confirmationUrl = "https://confirmacao-entrega-propria.ifood.com.br";

    // Abre imediatamente (sincrono no gesto do usuario) para evitar bloqueio de popup.
    const popup = window.open(confirmationUrl, '_blank', 'noopener,noreferrer');

    try {
        if (!iFoodId) return Boolean(popup);
        await navigator.clipboard.writeText(iFoodId);

        return true;
    } catch (err) {
        console.error("Falha ao automatizar confirmação iFood:", err);
        return Boolean(popup);
    }
};

/**
 * V3.5: Lógica determinística de aviso ao cliente.
 */
export const generateHailingStrategy = async (
    order: Order,
    driver: Driver,
    context: OperatingContext,
    systemConfig: SystemConfig,
    googleMapsApiKey?: string,
    manualTrigger?: string
): Promise<HailingStrategy> => {

    const firstName = order.customerName.split(' ')[0];

    if (!systemConfig.communication) {
        return {
            method: 'text',
            content: `Olá ${firstName}, o entregador ${driver.name} chegou!`,
            reasoning: "Configuração de comunicação ausente."
        };
    }

    const commConfig = systemConfig.communication;

    // 1. Determinação do Cenário Baseada em Regras (Priority Matrix)
    let scenario = 'Chegada Normal';
    let reasoning = 'Cenário padrão de entrega.';

    if (manualTrigger) {
        scenario = manualTrigger;
        reasoning = `Cenário selecionado manualmente: ${manualTrigger}.`;
    } else if (context.rainMode !== 'off') {
        scenario = 'Chuva';
        reasoning = 'Ajustado para condições de chuva.';
    } else if (context.isHighTraffic) {
        scenario = 'Trânsito';
        reasoning = 'Ajustado para trânsito intenso.';
    }

    // 2. Determinação do Método (Texto vs Áudio) baseado nas preferências
    const preference = commConfig.scenarioPreferences?.[scenario] || 'text';

    if (preference === 'audio') {
        const audioClip = commConfig.audioClips.find(c => c.scenario === scenario);
        if (audioClip) {
            return {
                method: 'audio',
                content: audioClip.id,
                suggestedAudioClip: audioClip,
                reasoning: reasoning + ' (Enviando Áudio)'
            };
        }
    }

    // 3. Seleção do Template de Texto
    const textTemplate = commConfig.textClips.find(t => t.scenario === scenario) ||
        commConfig.textClips.find(t => t.scenario === 'Chegada Normal');

    if (textTemplate) {
        return {
            method: 'text',
            content: hydrateTemplate(textTemplate.content, order, driver, systemConfig),
            reasoning: reasoning
        };
    }

    return {
        method: 'text',
        content: `Oi ${firstName}, aqui é o ${driver.name}. Seu pedido chegou!`,
        reasoning: 'Usando fallback de segurança.'
    };
};

export const shareAudioClip = async (clip: AudioClip): Promise<void> => {
    if (!navigator.share) {
        throw new Error("Compartilhamento não suportado neste dispositivo.");
    }

    const blob = base64ToBlob(clip.base64Data, clip.mimeType);
    const file = new File([blob], `${clip.name}`, { type: clip.mimeType });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
            files: [file],
            title: 'Aviso de Chegada',
            text: 'Olá! O entregador deixou um aviso para você.'
        });
    } else {
        throw new Error("O sistema não suporta o compartilhamento deste arquivo.");
    }
};

export const openWhatsAppText = (phone: string, text: string) => {
    const digits = phone.replace(/\D/g, '');
    const cleanPhone = digits.startsWith('55') ? digits : `55${digits}`;
    const encoded = encodeURIComponent(text);
    window.open(`https://wa.me/${cleanPhone}?text=${encoded}`, '_blank');
};
